import { useCallback, useEffect, useRef, useState } from 'react';

export const useDetectUserScroll = (
  scrollContainerRef: React.RefObject<HTMLDivElement> | null,
  rebindEvents: boolean = false,
) => {
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const scrollRef = useRef<{
    isProgrammatic: boolean;
    endTimeout: NodeJS.Timeout | null;
  }>({
    isProgrammatic: false,
    endTimeout: null,
  });

  useEffect(() => {
    if (!scrollContainerRef?.current) {
      return;
    }
    const scrollContainer = scrollContainerRef.current;

    let lastScrollTop = scrollContainer.scrollTop;

    const handleScroll = () => {
      const scrollState = scrollRef.current;
      const currentScrollTop = scrollContainer.scrollTop;
      const hasScrolled = currentScrollTop !== lastScrollTop;

      // If user manually scrolls (not programmatic), disable auto-scroll
      if (hasScrolled && !scrollState.isProgrammatic && isAutoScrollEnabled) {
        setIsAutoScrollEnabled(false);
      }

      // If programmatic scroll stopped (position unchanged), reset flag after delay
      if (scrollState.isProgrammatic && !hasScrolled) {
        if (scrollState.endTimeout) {
          clearTimeout(scrollState.endTimeout);
        }
        scrollState.endTimeout = setTimeout(() => {
          scrollState.isProgrammatic = false;
          scrollState.endTimeout = null;
        }, 100);
      }

      lastScrollTop = currentScrollTop;
    };

    const handleUserInput = () => {
      // If user interacts while programmatic scroll is active, reset it immediately
      if (scrollRef.current.isProgrammatic) {
        scrollRef.current.isProgrammatic = false;
        if (scrollRef.current.endTimeout) {
          clearTimeout(scrollRef.current.endTimeout);
          scrollRef.current.endTimeout = null;
        }
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    scrollContainer.addEventListener('mousedown', handleUserInput);
    scrollContainer.addEventListener('touchstart', handleUserInput, {
      passive: true,
    } as AddEventListenerOptions);
    scrollContainer.addEventListener('wheel', handleUserInput, {
      passive: true,
    });

    const scrollState = scrollRef.current;

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      scrollContainer.removeEventListener('mousedown', handleUserInput);
      scrollContainer.removeEventListener('touchstart', handleUserInput);
      scrollContainer.removeEventListener('wheel', handleUserInput);
      if (scrollState.endTimeout) {
        clearTimeout(scrollState.endTimeout);
      }
    };
  }, [scrollContainerRef, isAutoScrollEnabled, rebindEvents]);

  return {
    isAutoScrollEnabled,
    enableAutoScroll: useCallback(() => {
      setIsAutoScrollEnabled(true);
    }, []),
    startProgrammaticScroll: useCallback(() => {
      scrollRef.current.isProgrammatic = true;
    }, []),
  };
};
